import { useState, useRef, useCallback } from "react";
import { Upload, FileText, Loader2, Check, AlertTriangle, X, Sparkles, Copy, Calendar, Trash2, Pencil, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import CompareInvoicesModal from "@/components/invoices/CompareInvoicesModal";
import { EditInvoiceModal } from "@/components/invoices/EditInvoiceModal";

const STATUS = {
  QUEUED: "queued",
  UPLOADING: "uploading",
  DONE: "done",
  REVIEW: "review",
  DUPLICATE: "duplicate",
  SOFT_DUP: "soft_dup",
  REMOVED: "removed",
  OWN_INVOICE: "own_invoice",
  ERROR: "error",
};

// iter 61 · classify each item into the coarse buckets shown in the filter
// tabs. Kept module-level (pure) so it can be reused from tests.
function matchesFilter(item, filter) {
  if (filter === "all") return true;
  if (filter === "dup") return item.status === STATUS.DUPLICATE || item.status === STATUS.SOFT_DUP;
  if (filter === "review") return item.status === STATUS.REVIEW;
  if (filter === "error") return item.status === STATUS.ERROR || item.status === STATUS.OWN_INVOICE;
  if (filter === "ok") return item.status === STATUS.DONE;
  return true;
}

function countByFilter(items, filter) {
  return items.filter((i) => matchesFilter(i, filter)).length;
}

function formatMoney(value, currency = "EUR") {
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always" }).format(value || 0);
  } catch {
    return `${(value || 0).toFixed(2)} ${currency}`;
  }
}

export default function BulkUploadModal({ open, onOpenChange, onDone, mode = "expense" }) {
  const { t, lang } = useI18n();
  const fileInput = useRef(null);
  const [items, setItems] = useState([]); // [{id, file, status, invoice?, error?}]
  const [drag, setDrag] = useState(false);
  const [processing, setProcessing] = useState(false);
  // iter 60 — comparison + edit modals
  const [compareState, setCompareState] = useState(null); // { existing, fresh, itemId }
  const [editingInvoice, setEditingInvoice] = useState(null);
  // iter 61 — status filter + refresh
  const [statusFilter, setStatusFilter] = useState("all"); // all|dup|review|error|ok
  const [refreshing, setRefreshing] = useState(false);

  const isAlbaran = mode === "albaranes";
  const processEndpoint = isAlbaran ? "/albaranes/process-file" : "/invoices/process-file";

  // Context-aware title for /ingresos and /salarios and /albaranes
  const bulkTitle = isAlbaran
    ? (lang === "es" ? "Subida masiva de albaranes" : "Bulk upload of delivery notes")
    : mode === "income"
      ? t("bulkUploadIncome")
      : mode === "salaries"
        ? t("bulkUploadSalaries")
        : t("bulkUpload");

  const reset = useCallback(() => {
    setItems([]);
    setProcessing(false);
  }, []);

  const close = useCallback(
    (changed = false) => {
      if (processing) return;
      const wasDone = items.some((i) => i.status === STATUS.DONE || i.status === STATUS.REVIEW);
      onOpenChange(false);
      if (changed || wasDone) onDone?.();
      setTimeout(reset, 200);
    },
    [processing, items, onOpenChange, onDone, reset]
  );

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const accepted = Array.from(fileList).filter((f) => {
      const isOk = f.type.startsWith("image/") || f.type === "application/pdf";
      if (!isOk) toast.error(`${f.name}: ${lang === "es" ? "formato no soportado" : "unsupported format"}`);
      return isOk;
    });
    const newItems = accepted.map((file, idx) => ({
      id: `${Date.now()}-${idx}-${file.name}`,
      file,
      status: STATUS.QUEUED,
      invoice: null,
      error: null,
    }));
    setItems((prev) => [...prev, ...newItems]);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  // Change the date of a newly-created invoice that was flagged as soft-duplicate.
  // Updates the invoice via PUT and reflects the change in the row.
  const patchInvoiceDate = async (itemId, invoiceId, newDate) => {
    if (!invoiceId || !newDate) return;
    try {
      const { data } = await api.put(`/invoices/${invoiceId}`, { date: newDate });
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId
            ? { ...i, invoice: { ...i.invoice, date: newDate }, status: STATUS.DONE, duplicateOf: null }
            : i
        )
      );
      toast.success(lang === "es" ? `Fecha actualizada a ${newDate}` : `Date updated to ${newDate}`);
      onDone?.();
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Delete a newly-created invoice that was flagged as duplicate.
  const deleteNewInvoice = async (itemId, invoiceId) => {
    if (!invoiceId) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, status: STATUS.REMOVED, invoice: null, duplicateOf: null } : i
        )
      );
      toast.success(lang === "es" ? "Factura eliminada" : "Invoice deleted");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Iter 57 — Manual retry con Gemini 2.5 Pro para filas que Flash extrajo mal
  // (status REVIEW o ERROR). Reemplaza los campos en Mongo y refresca la fila
  // local con la nueva factura devuelta por el backend.
  const retryOcrPro = async (itemId, invoiceId) => {
    if (!invoiceId) return;
    const toastId = toast.loading(
      lang === "es" ? "Reintentando con IA avanzada…" : "Retrying with advanced AI…"
    );
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/retry-ocr-pro`);
      toast.dismiss(toastId);
      if (data?.ok) {
        toast.success(
          lang === "es" ? "IA avanzada: datos actualizados" : "Advanced AI: data updated"
        );
        // Update the row: if the new extraction fills vendor+amount, mark done;
        // otherwise keep it as review.
        const inv = data.invoice || {};
        const nextStatus = inv.needs_review ? STATUS.REVIEW : STATUS.DONE;
        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId ? { ...i, status: nextStatus, invoice: inv, error: null } : i
          )
        );
        onDone?.();
      } else {
        toast.error(data?.error || (lang === "es" ? "IA avanzada también falló" : "Advanced AI also failed"));
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(formatApiError(err));
    }
  };


  // iter 65/66 — "Registrar igualmente" para filas bloqueadas por CIF propio
  // (force_own) o por duplicado exacto SHA (force_dup). Re-envía el archivo
  // original al endpoint con los flags apropiados para que el backend salte
  // la validación correspondiente y persista la factura.
  const retryForce = async (itemId, { force_own = false, force_dup = false, prevStatus = STATUS.OWN_INVOICE } = {}) => {
    const target = items.find((i) => i.id === itemId);
    if (!target?.file) return;
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, status: STATUS.UPLOADING, error: null } : i))
    );
    try {
      const fd = new FormData();
      fd.append("file", target.file);
      const params = new URLSearchParams();
      if (force_own) params.set("force_own", "true");
      if (force_dup) params.set("force_dup", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.post(`${processEndpoint}${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let finalStatus;
      if (data.own_invoice) finalStatus = STATUS.OWN_INVOICE;
      else if (data.duplicate) finalStatus = STATUS.DUPLICATE;
      else if (data.soft_duplicate) finalStatus = STATUS.SOFT_DUP;
      else if (data.invoice && data.invoice.needs_review) finalStatus = STATUS.REVIEW;
      else if (data.error) finalStatus = STATUS.ERROR;
      else finalStatus = STATUS.DONE;
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId
            ? {
                ...i,
                status: finalStatus,
                invoice: data.invoice || null,
                duplicateOf: data.duplicate_of || data.soft_duplicate || null,
                error: data.error || null,
              }
            : i
        )
      );
      if (finalStatus === STATUS.DONE || finalStatus === STATUS.REVIEW) {
        toast.success(lang === "es" ? "Factura registrada" : "Invoice registered");
        onDone?.();
      } else if (finalStatus === STATUS.ERROR) {
        toast.error(data.error || (lang === "es" ? "No se pudo registrar" : "Could not register"));
      }
    } catch (err) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, status: prevStatus, error: formatApiError(err) } : i
        )
      );
      toast.error(formatApiError(err));
    }
  };


  // iter 61 · "Actualizar listado" — re-consulta cada item procesado con
  // invoice.id para reflejar cambios hechos manualmente (edición, borrado,
  // resolución de duplicado, etc.). Se aplica sólo a items con invoice
  // persistida — los REMOVED o QUEUED sin invoice se quedan como están.
  const refreshItems = useCallback(async () => {
    const toCheck = items.filter((i) => i.invoice?.id && i.status !== STATUS.REMOVED);
    if (toCheck.length === 0) return;
    setRefreshing(true);
    try {
      const results = await Promise.all(
        toCheck.map(async (i) => {
          try {
            const { data } = await api.get(`/invoices/${i.invoice.id}`);
            return { itemId: i.id, invoice: data, missing: false };
          } catch (err) {
            const status = err?.response?.status;
            return { itemId: i.id, invoice: null, missing: status === 404 };
          }
        })
      );
      setItems((prev) =>
        prev.map((i) => {
          const upd = results.find((r) => r.itemId === i.id);
          if (!upd) return i;
          if (upd.missing) return { ...i, status: STATUS.REMOVED, invoice: null };
          if (!upd.invoice) return i;
          let next = i.status;
          if (upd.invoice.needs_review) next = STATUS.REVIEW;
          else if (i.status === STATUS.REVIEW || i.status === STATUS.ERROR) {
            next = STATUS.DONE;
          }
          return { ...i, invoice: upd.invoice, status: next, error: null };
        })
      );
      toast.success(lang === "es" ? "Listado actualizado" : "List refreshed");
    } finally {
      setRefreshing(false);
    }
  }, [items, lang]);

  const processAll = async () => {
    const queued = items.filter((i) => i.status === STATUS.QUEUED);
    if (queued.length === 0) return;


    setProcessing(true);

    // Process up to 3 in parallel for snappier UX
    const concurrency = 3;
    const queue = [...queued];
    // Track soft-dup hits from worker results — using `items` after the loop
    // reads a stale closure snapshot (still all QUEUED) and never fires the
    // warning. See code review iter 62.
    let softDupsSeen = 0;

    const worker = async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (!next) break;
        setItems((prev) => prev.map((i) => (i.id === next.id ? { ...i, status: STATUS.UPLOADING } : i)));
        try {
          const fd = new FormData();
          fd.append("file", next.file);
          const { data } = await api.post(processEndpoint, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          let finalStatus;
          const entity = isAlbaran ? data.albaran : data.invoice;
          if (!isAlbaran && data.own_invoice) finalStatus = STATUS.OWN_INVOICE;
          else if (!isAlbaran && data.duplicate) finalStatus = STATUS.DUPLICATE;
          else if (!isAlbaran && data.soft_duplicate) finalStatus = STATUS.SOFT_DUP;
          else if (entity && entity.needs_review) finalStatus = STATUS.REVIEW;
          else if (data.error) finalStatus = STATUS.ERROR;
          else finalStatus = STATUS.DONE;
          if (finalStatus === STATUS.SOFT_DUP) softDupsSeen += 1;
          // iter 65 — when the backend flags own_invoice we don't have a
          // persisted invoice yet, but the OCR preview travels in `extracted`
          // so we can show vendor/date/amount alongside the "Register anyway"
          // button.
          const ocrPreview = data.own_invoice && data.extracted
            ? {
                vendor: data.extracted.vendor || null,
                date: data.extracted.date || null,
                amount: data.extracted.total ?? data.extracted.amount ?? null,
                currency: data.extracted.currency || "EUR",
              }
            : null;
          setItems((prev) =>
            prev.map((i) =>
              i.id === next.id
                ? {
                    ...i,
                    status: finalStatus,
                    invoice: entity || ocrPreview,  // reused: invoice, albaran, or own-invoice preview
                    duplicateOf: data.duplicate_of || data.soft_duplicate || null,
                    error: data.error || data.message || null,
                  }
                : i
            )
          );
        } catch (err) {
          setItems((prev) =>
            prev.map((i) =>
              i.id === next.id
                ? { ...i, status: STATUS.ERROR, error: formatApiError(err) }
                : i
            )
          );
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, queued.length) }, () => worker()));
    setProcessing(false);
    // Prominent alert when soft-duplicates were detected so the user reviews them
    // before closing the modal (matches "modal antes de guardar" request — this
    // gives the chance to fix a wrong date extracted by OCR or delete the copy).
    if (softDupsSeen > 0) {
      toast.warning(
        lang === "es"
          ? `${softDupsSeen} posible(s) duplicado(s) detectado(s). Revisa las filas marcadas: puedes cambiar la fecha o eliminar la copia.`
          : `${softDupsSeen} possible duplicate(s) detected. Review flagged rows: change date or delete the copy.`,
        { duration: 8000 }
      );
    } else {
      toast.success(lang === "es" ? "Procesamiento completado" : "Processing complete");
    }
    onDone?.();
  };

  const queuedCount = items.filter((i) => i.status === STATUS.QUEUED).length;
  const doneCount = items.filter((i) => i.status === STATUS.DONE).length;
  const reviewCount = items.filter((i) => i.status === STATUS.REVIEW).length;
  const dupCount = items.filter((i) => i.status === STATUS.DUPLICATE).length;
  const softDupCount = items.filter((i) => i.status === STATUS.SOFT_DUP).length;
  const errorCount = items.filter((i) => i.status === STATUS.ERROR).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles size={20} className="text-[var(--primary)]" />
            {bulkTitle}
          </DialogTitle>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t("bulkUploadHint")}</p>
        </DialogHeader>

        {/* Dropzone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInput.current?.click()}
          className={`dropzone rounded-lg p-8 text-center cursor-pointer ${drag ? "active" : ""}`}
          data-testid="bulk-dropzone"
        >
          <Upload className="mx-auto mb-2 text-[var(--text-muted)]" size={24} strokeWidth={1.5} />
          <p className="text-sm text-[var(--text-secondary)]">
            {lang === "es"
              ? "Arrastra varios archivos o haz clic para seleccionar"
              : "Drag multiple files here, or click to select"}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-1">PDF, PNG, JPG · max 15MB c/u</p>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = ""; // allow reselecting the same file
            }}
            data-testid="bulk-file-input"
          />
        </div>

        {/* Iter 61 · Header con contadores, filtro por estado y botón refresh */}
        {items.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap gap-1 items-center" role="tablist" aria-label={lang === "es" ? "Filtrar por estado" : "Filter by status"}>
              {[
                { k: "all",    label: lang === "es" ? "Todas"       : "All" },
                { k: "dup",    label: lang === "es" ? "Duplicadas"  : "Duplicates" },
                { k: "review", label: lang === "es" ? "En revisión" : "Review" },
                { k: "error",  label: lang === "es" ? "Error"       : "Errors" },
                { k: "ok",     label: lang === "es" ? "OK"          : "OK" },
              ].map((tab) => {
                const count = countByFilter(items, tab.k);
                const active = statusFilter === tab.k;
                return (
                  <button
                    key={tab.k}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setStatusFilter(tab.k)}
                    data-testid={`bulk-filter-${tab.k}`}
                    className={`px-2 py-1 rounded text-xs border transition-colors ${
                      active
                        ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                        : "border-[var(--border-color)] hover:bg-[var(--surface-hover)]"
                    }`}
                  >
                    {tab.label}
                    <span className={`ml-1 text-[10px] ${active ? "opacity-80" : "text-[var(--text-muted)]"}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={refreshItems}
              disabled={refreshing || processing}
              data-testid="bulk-refresh-btn"
              className="h-8 text-xs"
              title={lang === "es"
                ? "Volver a consultar el estado de las facturas procesadas"
                : "Re-check the status of processed invoices"}
            >
              {refreshing ? (
                <Loader2 size={12} className="mr-1 animate-spin" />
              ) : (
                <ArrowRightLeft size={12} className="mr-1" />
              )}
              {lang === "es" ? "Actualizar" : "Refresh"}
            </Button>
          </div>
        )}

        {/* List */}
        {items.length > 0 && (
          <div className="mt-2 border border-[var(--border-color)] rounded-lg divide-y divide-[var(--border-color)] overflow-hidden">
            {items.filter((it) => matchesFilter(it, statusFilter)).map((item) => (
              <BulkRow
                key={item.id}
                item={item}
                onRemove={() => removeItem(item.id)}
                processing={processing}
                onChangeDate={(newDate) => patchInvoiceDate(item.id, item.invoice?.id, newDate)}
                onDeleteInvoice={() => deleteNewInvoice(item.id, item.invoice?.id)}
                onRetryPro={() => retryOcrPro(item.id, item.invoice?.id)}
                onForceOwn={() => retryForce(item.id, { force_own: true, prevStatus: STATUS.OWN_INVOICE })}
                onForceDup={() => retryForce(item.id, { force_dup: true, prevStatus: STATUS.DUPLICATE })}
                onCompare={() => {
                  // Soft-dup: fresh + existing side-by-side.
                  // Hard-dup: only existing (fresh rejected). Modal hides
                  // the "new upload" column via its hardDup branch.
                  if (item.duplicateOf) {
                    setCompareState({
                      existing: item.duplicateOf,
                      fresh: item.invoice || null,
                      itemId: item.id,
                    });
                  }
                }}
                onEdit={() => {
                  // For hard-dup rows the freshly uploaded file was rejected
                  // pre-OCR (no invoice persisted); edit the existing dup
                  // instead so the user can still tweak the record on disk.
                  const target = item.invoice || item.duplicateOf;
                  if (target) setEditingInvoice(target);
                }}
                onDismiss={() => {
                  // Local-only dismissal: no invoice was persisted on the server
                  // (exact duplicate or unrecoverable error). Just hide the row.
                  setItems((prev) =>
                    prev.map((i) =>
                      i.id === item.id ? { ...i, status: STATUS.REMOVED, invoice: null, duplicateOf: null, error: null } : i
                    )
                  );
                }}
              />
            ))}
          </div>
        )}

        {/* Summary */}
        {items.length > 0 && (
          <div className="mt-3 flex items-center gap-3 text-xs text-[var(--text-secondary)]">
            {queuedCount > 0 && (
              <span data-testid="count-queued">
                {queuedCount} {lang === "es" ? "en cola" : "queued"}
              </span>
            )}
            {doneCount > 0 && (
              <span className="text-[var(--status-positive)]" data-testid="count-done">
                <Check size={12} className="inline mr-0.5" />
                {doneCount} {lang === "es" ? "creadas" : "created"}
              </span>
            )}
            {reviewCount > 0 && (
              <span className="text-[var(--status-warning)]" data-testid="count-review">
                <AlertTriangle size={12} className="inline mr-0.5" />
                {reviewCount} {lang === "es" ? "a revisar" : "to review"}
              </span>
            )}
            {dupCount > 0 && (
              <span className="text-[var(--status-duplicate)]" data-testid="count-duplicate">
                <Copy size={12} className="inline mr-0.5" />
                {dupCount} {lang === "es" ? "duplicadas" : "duplicates"}
              </span>
            )}
            {softDupCount > 0 && (
              <span className="text-[var(--status-duplicate)]" data-testid="count-soft-dup">
                <Copy size={12} className="inline mr-0.5" />
                {softDupCount} {lang === "es" ? "posibles duplicados" : "possible duplicates"}
              </span>
            )}
            {errorCount > 0 && (
              <span className="text-[var(--status-negative)]" data-testid="count-error">
                <X size={12} className="inline mr-0.5" />
                {errorCount} {lang === "es" ? "con error" : "failed"}
              </span>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => close()} disabled={processing} data-testid="bulk-close-btn">
            {processing ? t("cancel") : (doneCount + reviewCount > 0 ? (lang === "es" ? "Cerrar" : "Close") : t("cancel"))}
          </Button>
          <Button
            onClick={processAll}
            disabled={processing || queuedCount === 0}
            data-testid="bulk-process-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={14} />
                {lang === "es" ? "Procesando..." : "Processing..."}
              </>
            ) : (
              <>
                <Sparkles className="mr-2" size={14} />
                {lang === "es" ? `Procesar ${queuedCount} archivo${queuedCount === 1 ? "" : "s"}` : `Process ${queuedCount} file${queuedCount === 1 ? "" : "s"}`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* iter 60 — comparison modal for duplicates + edit modal for any AI row */}
      <CompareInvoicesModal
        open={!!compareState}
        onOpenChange={(v) => { if (!v) setCompareState(null); }}
        existing={compareState?.existing}
        fresh={compareState?.fresh}
        lang={lang}
        busy={processing}
        onEditExisting={() => {
          const existing = compareState?.existing;
          setCompareState(null);
          if (existing) setEditingInvoice(existing);
        }}
        onEditNew={() => {
          const fresh = compareState?.fresh;
          setCompareState(null);
          if (fresh) setEditingInvoice(fresh);
        }}
        onReplace={async () => {
          const keepId = compareState?.existing?.id;
          const dropId = compareState?.fresh?.id;
          if (!keepId || !dropId) return;
          try {
            await api.post(`/invoices/${keepId}/replace-with/${dropId}`);
            toast.success(lang === "es" ? "Factura actualizada" : "Invoice updated");
            setItems((prev) => prev.map((i) =>
              i.id === compareState.itemId ? { ...i, status: STATUS.REMOVED } : i
            ));
            onDone?.();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setCompareState(null);
          }
        }}
        onDeleteNew={async () => {
          const dropId = compareState?.fresh?.id;
          if (!dropId) return;
          try {
            await api.delete(`/invoices/${dropId}`);
            toast.success(lang === "es" ? "Duplicado eliminado" : "Duplicate deleted");
            setItems((prev) => prev.map((i) =>
              i.id === compareState.itemId ? { ...i, status: STATUS.REMOVED } : i
            ));
            onDone?.();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setCompareState(null);
          }
        }}
        onKeepBoth={() => setCompareState(null)}
        onDismiss={() => {
          // hard-dup compare: user clicked "Descartar" from within the modal.
          // Mark row as removed locally (no server-side invoice existed).
          const itemId = compareState?.itemId;
          setCompareState(null);
          if (itemId) {
            setItems((prev) =>
              prev.map((i) =>
                i.id === itemId ? { ...i, status: STATUS.REMOVED, invoice: null, duplicateOf: null, error: null } : i
              )
            );
          }
        }}
      />
      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          categories={[]}
          onClose={() => setEditingInvoice(null)}
          onSaved={() => {
            setEditingInvoice(null);
            onDone?.();
            toast.success(lang === "es" ? "Factura guardada" : "Invoice saved");
          }}
        />
      )}
    </Dialog>
  );
}

function BulkRow({ item, onRemove, processing, onChangeDate, onDeleteInvoice, onDismiss, onRetryPro, onCompare, onEdit, onForceOwn, onForceDup }) {
  const { lang } = useI18n();
  const { file, status, invoice, duplicateOf, error } = item;
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState(invoice?.date || "");
  const [busy, setBusy] = useState(false);

  const isSoftDup = status === STATUS.SOFT_DUP;
  const isReview = status === STATUS.REVIEW;
  const isDone = status === STATUS.DONE;
  const isError = status === STATUS.ERROR;
  const isDuplicate = status === STATUS.DUPLICATE;
  const isOwnInvoice = status === STATUS.OWN_INVOICE;
  // A row is "resolvable" (user can remove/dismiss it) after processing finished.
  // If the invoice was persisted on the server (invoice.id) we call DELETE on the
  // backend; otherwise we just dismiss the row locally.
  const canServerDelete = !!invoice?.id && (isDone || isReview || isSoftDup || isError);
  const canDismiss = !canServerDelete && (isDuplicate || isError || isOwnInvoice);

  const applyDate = async () => {
    if (!dateDraft) return;
    setBusy(true);
    try {
      await onChangeDate?.(dateDraft);
      setEditingDate(false);
    } finally {
      setBusy(false);
    }
  };

  const removeInvoice = async () => {
    setBusy(true);
    try {
      await onDeleteInvoice?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3" data-testid={`bulk-row-${status}`}>
      <div className="flex items-center gap-3">
        <FileText size={18} className="text-[var(--text-muted)] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{file.name}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {(file.size / 1024).toFixed(1)} KB
            {invoice && status !== STATUS.DUPLICATE && status !== STATUS.REMOVED && (
              <>
                {" · "}
                <span>{invoice.vendor || "—"}</span>
                {" · "}
                <span className="font-mono-num">{invoice.date || "—"}</span>
                {" · "}
                <span className="font-mono-num">{formatMoney(invoice.amount ?? invoice.total, invoice.currency)}</span>
              </>
            )}
            {duplicateOf && (
              <>
                {" · "}
                <span className="text-[var(--status-duplicate)]">
                  {lang === "es" ? "ya existe como" : "already exists as"}{" "}
                  {duplicateOf.vendor || "—"} · {duplicateOf.date || "—"} · {formatMoney(duplicateOf.amount, duplicateOf.currency)}
                </span>
                {duplicateOf.id && (
                  <>
                    {" · "}
                    <a
                      href={`/invoices?focus=${duplicateOf.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--brand-accent)] hover:underline"
                      data-testid={`bulk-open-existing-${item.id}`}
                    >
                      {lang === "es" ? "abrir en listado ↗" : "open in list ↗"}
                    </a>
                  </>
                )}
              </>
            )}
            {error && <span className="text-[var(--status-negative)]"> · {error}</span>}
          </p>
        </div>
        <StatusBadge status={status} lang={lang} />
        {status === STATUS.QUEUED && !processing && (
          <button
            onClick={onRemove}
            className="text-[var(--text-muted)] hover:text-[var(--status-negative)] transition-colors"
            data-testid="bulk-remove-row"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* iter 60/62 — Compare button available whenever we have a duplicate
          counterpart. For soft-dup we compare fresh vs existing; for hard-dup
          `invoice` is null (file rejected pre-OCR) so we open the same modal
          in single-column mode (see CompareInvoicesModal's `hardDup` branch). */}
      {(isSoftDup || isDuplicate) && duplicateOf && onCompare && (
        <div className="mt-2 ml-8">
          <Button
            size="sm"
            variant="outline"
            onClick={onCompare}
            disabled={busy}
            className="h-7 text-xs"
            data-testid={`bulk-compare-${item.id}`}
          >
            <ArrowRightLeft size={12} className="mr-1" />
            {lang === "es" ? "Comparar" : "Compare"}
          </Button>
        </div>
      )}

      {/* Actions for soft-duplicate rows: change date or delete */}
      {isSoftDup && invoice?.id && (
        <div className="mt-2 ml-8 flex items-center gap-2 flex-wrap">
          {editingDate ? (
            <>
              <Input
                type="date"
                value={dateDraft}
                onChange={(e) => setDateDraft(e.target.value)}
                className="h-8 w-40"
                data-testid={`bulk-date-input-${item.id}`}
              />
              <Button
                size="sm"
                onClick={applyDate}
                disabled={busy || !dateDraft || dateDraft === invoice.date}
                className="h-8 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                data-testid={`bulk-date-save-${item.id}`}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : (lang === "es" ? "Guardar" : "Save")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setEditingDate(false); setDateDraft(invoice?.date || ""); }}
                disabled={busy}
                className="h-8"
                data-testid={`bulk-date-cancel-${item.id}`}
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
            </>
          ) : (
            <>
              <span className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "¿Es un duplicado real o solo cambió la fecha?"
                  : "Real duplicate or just wrong date?"}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setDateDraft(invoice.date || ""); setEditingDate(true); }}
                disabled={busy}
                className="h-7 text-xs"
                data-testid={`bulk-change-date-${item.id}`}
              >
                <Calendar size={12} className="mr-1" />
                {lang === "es" ? "Cambiar fecha" : "Change date"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={removeInvoice}
                disabled={busy}
                className="h-7 text-xs text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
                data-testid={`bulk-delete-dup-${item.id}`}
              >
                <Trash2 size={12} className="mr-1" />
                {lang === "es" ? "Eliminar" : "Delete"}
              </Button>
            </>
          )}
        </div>
      )}

      {/* iter 65 — Bypass "CIF propio": permite forzar el registro cuando
          el OCR detectó el CIF del tenant como proveedor (falso positivo o
          excepción legítima). Reenvía el archivo con force_own=true. */}
      {isOwnInvoice && (
        <div className="mt-2 ml-8 flex items-center gap-2 flex-wrap" data-testid={`bulk-own-actions-${item.id}`}>
          <span className="text-xs text-[var(--text-muted)]">
            {lang === "es"
              ? "El CIF detectado coincide con el tuyo."
              : "Detected CIF matches your own."}
          </span>
          <Button
            size="sm"
            onClick={async () => {
              setBusy(true);
              try {
                await onForceOwn?.();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className="h-7 text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid={`bulk-force-own-${item.id}`}
          >
            {busy ? (
              <Loader2 size={12} className="mr-1 animate-spin" />
            ) : (
              <Check size={12} className="mr-1" />
            )}
            {lang === "es" ? "Registrar igualmente" : "Register anyway"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDismiss}
            disabled={busy}
            className="h-7 text-xs"
            data-testid={`bulk-own-dismiss-${item.id}`}
          >
            <Trash2 size={12} className="mr-1" />
            {lang === "es" ? "Descartar" : "Dismiss"}
          </Button>
        </div>
      )}

      {/* Generic delete/dismiss action for done/review/error/duplicate rows */}
      {!isSoftDup && !isOwnInvoice && (canServerDelete || canDismiss) && (
        <div className="mt-2 ml-8 flex items-center gap-2" data-testid={`bulk-row-actions-${status}`}>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              setBusy(true);
              try {
                if (canServerDelete) await onDeleteInvoice?.();
                else onDismiss?.();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className="h-7 text-xs text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
            data-testid={`bulk-remove-${status}-${item.id}`}
          >
            <Trash2 size={12} className="mr-1" />
            {canServerDelete
              ? (lang === "es" ? "Eliminar factura" : "Delete invoice")
              : (lang === "es" ? "Descartar" : "Dismiss")}
          </Button>
          {isReview && invoice?.id && (
            <a
              href={`/invoices?focus=${invoice.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--primary)] hover:underline inline-flex items-center gap-1"
              data-testid={`bulk-review-open-${item.id}`}
            >
              {lang === "es" ? "Revisar factura →" : "Review invoice →"}
            </a>
          )}
          {/* Iter 57 — Manual retry with Gemini 2.5 Pro. Only offered for
              REVIEW or ERROR rows where an invoice was actually persisted
              (server has a file_id we can re-extract from). */}
          {(isReview || isError) && invoice?.id && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                setBusy(true);
                try {
                  await onRetryPro?.();
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="h-7 text-xs border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10"
              data-testid={`bulk-retry-pro-${item.id}`}
            >
              <Sparkles size={12} className="mr-1" />
              {lang === "es" ? "Reintentar con IA avanzada" : "Retry with advanced AI"}
            </Button>
          )}
          {/* iter 66 — Force-save button for hard duplicates. Handles the
              "duplicada pero no aparece" case (orphan file records) and lets
              the user force-ingest a real duplicate when they need a copy. */}
          {isDuplicate && onForceDup && (
            <Button
              size="sm"
              onClick={async () => {
                setBusy(true);
                try {
                  await onForceDup?.();
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="h-7 text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid={`bulk-force-dup-${item.id}`}
            >
              {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Check size={12} className="mr-1" />}
              {lang === "es" ? "Registrar igualmente" : "Register anyway"}
            </Button>
          )}
          {/* iter 60/62 — Edit button visible on ANY AI-processed row where
              we have a persisted invoice to edit. For hard-dup we open the
              existing dup (duplicateOf) instead of the (never-persisted)
              fresh one. */}
          {((invoice?.id && (isDone || isReview || isError || isSoftDup)) ||
            (isDuplicate && duplicateOf?.id)) && onEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              disabled={busy}
              className="h-7 text-xs"
              data-testid={`bulk-edit-${item.id}`}
            >
              <Pencil size={12} className="mr-1" />
              {lang === "es" ? "Editar" : "Edit"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, lang }) {
  const map = {
    [STATUS.QUEUED]: {
      label: lang === "es" ? "En cola" : "Queued",
      cls: "bg-[var(--surface-hover)] text-[var(--text-secondary)]",
      icon: null,
    },
    [STATUS.UPLOADING]: {
      label: lang === "es" ? "Procesando" : "Processing",
      cls: "bg-[var(--status-warning-bg)] text-[var(--status-warning)]",
      icon: <Loader2 size={10} className="animate-spin" />,
    },
    [STATUS.DONE]: {
      label: lang === "es" ? "Creada" : "Created",
      cls: "bg-[var(--status-positive-bg)] text-[var(--status-positive)]",
      icon: <Check size={10} />,
    },
    [STATUS.REVIEW]: {
      label: lang === "es" ? "Revisar" : "Review",
      cls: "bg-[var(--status-warning-bg)] text-[var(--status-warning)]",
      icon: <AlertTriangle size={10} />,
    },
    [STATUS.DUPLICATE]: {
      label: lang === "es" ? "Duplicada" : "Duplicate",
      cls: "bg-[var(--status-duplicate-bg)] text-[var(--status-duplicate)]",
      icon: <Copy size={10} />,
    },
    [STATUS.SOFT_DUP]: {
      label: lang === "es" ? "Posible duplicado" : "Possible duplicate",
      cls: "bg-[var(--status-duplicate-bg)] text-[var(--status-duplicate)]",
      icon: <Copy size={10} />,
    },
    [STATUS.REMOVED]: {
      label: lang === "es" ? "Eliminada" : "Removed",
      cls: "bg-[var(--surface-hover)] text-[var(--text-secondary)]",
      icon: <Trash2 size={10} />,
    },
    [STATUS.OWN_INVOICE]: {
      label: lang === "es" ? "Tu propia factura" : "Your own invoice",
      cls: "bg-[var(--status-negative-bg)] text-[var(--status-negative)]",
      icon: <AlertTriangle size={10} />,
    },
    [STATUS.ERROR]: {
      label: lang === "es" ? "Error" : "Failed",
      cls: "bg-[var(--status-negative-bg)] text-[var(--status-negative)]",
      icon: <X size={10} />,
    },
  };
  const s = map[status] || map[STATUS.QUEUED];
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${s.cls}`}>
      {s.icon}
      {s.label}
    </span>
  );
}
